import { Popover, PopoverTrigger, PopoverContent, PopoverPortal } from "@radix-ui/react-popover";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import IconWorld from "central-icons/IconWorld";
import IconGithub from "central-icons/IconGithub";
import IconCirclePlus from "central-icons/IconCirclePlus";
import { Command, useCommandState } from "cmdk";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { atom, useAtom, useAtomValue } from "jotai";
import {
    useSearchRepositoriesPaginated,
    useIsUserAuthenticatedWithRunnerForHost,
    type PlainSearchRepository,
} from "@/queries/runner-queries";
import { Skeleton } from "@/components/Skeleton";
import { SearchMode } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useDebounce } from "use-debounce";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { formatError } from "@/utils/errors";
import { useSegmentTrack } from "@/hooks/use-segment";
import { ConnectError, Code } from "@connectrpc/connect";
import { isValidRepoUrl } from "@/utils/url-validation";

type RepositorySelectorPopoverProps = {
    runnerId: string;
    scmHost: string;
    value?: string;
    onSelect: (repoUrl: string, repoName: string) => void;
    disabled?: boolean;
    className?: string;
    triggerClassName?: string;
    placeholder?: string;
    buttonLabel?: string;
    trackingLocation?: string;
    /** Alignment of the popover relative to the trigger. Defaults to "start". */
    align?: "start" | "center" | "end";
};

export const RepositorySelectorPopover: FC<RepositorySelectorPopoverProps> = ({
    runnerId,
    scmHost,
    value,
    onSelect,
    disabled = false,
    className,
    triggerClassName,
    placeholder = "Type to search for private repositories",
    buttonLabel,
    trackingLocation = "repository-selector",
    align = "start",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const segmentTrack = useSegmentTrack();

    const handleSelect = useCallback(
        (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => {
            onSelect(repoUrl, repoName);
            setIsOpen(false);

            // Track repository selection
            segmentTrack("repository_selected", {
                location: trackingLocation,
                repository_name: repoName,
                repository_url: repoUrl,
                selection_index: selectionIndex,
                total_available: totalAvailable,
            });
        },
        [onSelect, segmentTrack, trackingLocation],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (open) {
                segmentTrack("repository_search_opened", {
                    location: trackingLocation,
                });
            }
        },
        [segmentTrack, trackingLocation],
    );

    // Use nullish coalescing so empty string means "icon only" (no label)
    const displayLabel = buttonLabel ?? value ?? "Select repository";

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        "flex h-9 items-center justify-between gap-2",
                        "rounded-lg border border-border-input-default bg-surface-input px-3",
                        "text-base text-content-primary",
                        "transition-all duration-150 ease-out",
                        "outline-none",
                        "hover:border-border-input-active",
                        "focus:border-border-input-active focus:ring-1 focus:ring-border-brand focus:ring-offset-1",
                        "aria-expanded:border-border-input-active aria-expanded:ring-4 aria-expanded:ring-ring-default aria-expanded:ring-offset-0",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        triggerClassName,
                    )}
                    disabled={disabled}
                    data-tracking-id={`${trackingLocation}-trigger`}
                >
                    <span className="flex items-center gap-2 truncate">
                        <IconWorld className="size-4 flex-shrink-0" />
                        {!!displayLabel && <span className="truncate">{displayLabel}</span>}
                    </span>
                    <IconChevronDownSmall className="size-5 flex-shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverPortal>
                <PopoverContent
                    align={align}
                    sideOffset={4}
                    className={cn("z-50 w-[450px] min-w-[var(--radix-popover-trigger-width)]", className)}
                >
                    <RepositorySelectorMenu
                        runnerId={runnerId}
                        scmHost={scmHost}
                        onSelect={handleSelect}
                        placeholder={placeholder}
                        trackingLocation={trackingLocation}
                    />
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};

type RepositorySelectorMenuProps = {
    runnerId: string;
    scmHost: string;
    onSelect: (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => void;
    placeholder?: string;
    trackingLocation?: string;
};

const RepositorySelectorMenu: FC<RepositorySelectorMenuProps> = ({
    runnerId,
    scmHost,
    onSelect,
    placeholder = "Type to search for private repositories",
    trackingLocation = "repository-selector",
}) => {
    // Create instance-specific atom to prevent cross-component interference
    const searchQueryAtom = useMemo(() => atom(""), []);

    return (
        <div className="rounded-lg border border-border-base bg-surface-popover shadow-md dark:bg-[rgb(var(--ona-gray-700))]">
            <Command>
                <div className="flex items-center border-b border-b-border-base">
                    <IconMagnifyingGlass className="ml-3 size-4" />
                    <RepositorySearchInput placeholder={placeholder} searchQueryAtom={searchQueryAtom} />
                </div>
                <div
                    className="max-h-32 overflow-y-auto px-1"
                    onWheel={(e) => {
                        // Prevent Dialog's scroll lock from intercepting wheel events
                        // This allows mouse wheel scrolling to work inside the popover
                        e.stopPropagation();
                    }}
                >
                    <Command.List label="Repositories">
                        <RepositoryCommandGroup
                            runnerId={runnerId}
                            scmHost={scmHost}
                            onSelectRepo={onSelect}
                            trackingLocation={trackingLocation}
                            searchQueryAtom={searchQueryAtom}
                        />
                    </Command.List>
                </div>
                <div className="text-foreground-muted border-t border-t-border-base px-3 py-2 text-sm">
                    <span>
                        Can&apos;t find the repository you&apos;re looking for?{" "}
                        <a
                            href="https://ona.com/docs/ona/source-control/github#granting-access-to-additional-github-organizations"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-content-brand hover:underline"
                        >
                            Grant organization access
                        </a>
                    </span>
                </div>
            </Command>
        </div>
    );
};

const RepositorySearchInput: FC<{ placeholder: string; searchQueryAtom: ReturnType<typeof atom<string>> }> = ({
    placeholder,
    searchQueryAtom,
}) => {
    const [value, setValue] = useAtom(searchQueryAtom);
    return (
        <Command.Input
            autoFocus
            value={value}
            onValueChange={setValue}
            className="w-full flex-1 bg-transparent py-3 pl-2 pr-3 text-base focus:outline-none focus:ring-0"
            placeholder={placeholder}
        />
    );
};

const RepositoryCommandGroup: FC<{
    runnerId: string;
    scmHost: string;
    onSelectRepo: (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => void;
    trackingLocation?: string;
    searchQueryAtom: ReturnType<typeof atom<string>>;
}> = ({ runnerId, scmHost, onSelectRepo, trackingLocation = "repository-selector", searchQueryAtom }) => {
    const [shouldShowUserRepos, setShouldShowUserRepos] = useState(false);
    const queryKeyword = useAtomValue(searchQueryAtom);
    const [debouncedQueryKeyword, { isPending: isQueryDebouncePending }] = useDebounce(queryKeyword, 200);
    const hasNoLocalResults = useCommandState((state) => state.filtered.count === 0);
    const segmentTrack = useSegmentTrack();

    const {
        data: repositoriesData,
        status,
        error: searchQueryError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useSearchRepositoriesPaginated({
        scmHost,
        runnerId,
        query: shouldShowUserRepos ? "" : debouncedQueryKeyword,
        searchMode: SearchMode.KEYWORD,
    });

    // Flatten paginated results
    const repositories = useMemo(
        () => repositoriesData?.pages.flatMap((page) => page.repositories) ?? [],
        [repositoriesData],
    );

    // useCallback because it is passed to a memoed component
    const _onSelectRepo = useCallback(
        (repoUrl: string, repoName: string, selectionIndex: number) => {
            onSelectRepo(repoUrl, repoName, selectionIndex, repositories?.length ?? 0);
        },
        [onSelectRepo, repositories],
    );

    // this useEffect determines whether to filter user's repositories or to perform a network query:
    // local search is always prioritized over network search, until there is no more local results to show
    // if there are no local results and the query is not empty, we should perform a network query
    // if the query is reset back to empty, we should show the user's repositories
    useEffect(() => {
        if (hasNoLocalResults && debouncedQueryKeyword) {
            setShouldShowUserRepos(false);
        } else if (!shouldShowUserRepos && !debouncedQueryKeyword) {
            setShouldShowUserRepos(true);
        }
    }, [debouncedQueryKeyword, hasNoLocalResults, shouldShowUserRepos]);

    // Track when repositories are loaded (only once per query)
    const hasTrackedRef = useRef(false);
    const lastQueryRef = useRef(debouncedQueryKeyword);
    const lastCountRef = useRef(0);

    useEffect(() => {
        const currentCount = repositories?.length ?? 0;

        // Reset tracking flag when query changes or count changes
        if (lastQueryRef.current !== debouncedQueryKeyword || lastCountRef.current !== currentCount) {
            hasTrackedRef.current = false;
            lastQueryRef.current = debouncedQueryKeyword;
            lastCountRef.current = currentCount;
        }

        // Track only on initial load for this query/count combination
        if (repositories && currentCount > 0 && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            segmentTrack("repositories_loaded", {
                location: trackingLocation,
                repository_count: currentCount,
                repository_names: repositories.map((repo) => repo.name),
                has_search_query: Boolean(debouncedQueryKeyword),
            });
        }
    }, [repositories, debouncedQueryKeyword, segmentTrack, trackingLocation]);

    if (isQueryDebouncePending() && hasNoLocalResults) {
        return <LoadingSkeleton />;
    }

    switch (status) {
        case "pending":
            return (
                <Command.Loading>
                    <LoadingSkeleton />
                </Command.Loading>
            );
        case "success":
            return (
                <>
                    <Command.Empty className="px-3 py-2 text-base opacity-50">No repositories found</Command.Empty>
                    <Command.Group className="py-1">
                        <CreateFromUrlItem searchQuery={debouncedQueryKeyword} onSelect={_onSelectRepo} />
                        {repositories.length > 0 && (
                            <RepositoryList
                                repositories={repositories}
                                onSelect={_onSelectRepo}
                                hasNextPage={hasNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                                onLoadMore={() => void fetchNextPage()}
                            />
                        )}
                    </Command.Group>
                </>
            );
        case "error": {
            // Check if it's an authentication error
            const isAuthError =
                searchQueryError instanceof ConnectError && searchQueryError.code === Code.FailedPrecondition;

            if (isAuthError) {
                return (
                    <AuthenticationRequired
                        runnerId={runnerId}
                        scmHost={scmHost}
                        searchQuery={debouncedQueryKeyword}
                        onSelectUrl={_onSelectRepo}
                    />
                );
            }

            return (
                <p className="my-1 w-full text-wrap rounded bg-red-50 px-3 py-1 text-base text-red-500 dark:bg-red-900 dark:text-red-100">
                    {formatError(searchQueryError)}
                </p>
            );
        }
    }
};

const LoadingSkeleton: FC = memo(() => {
    return (
        <div className="space-y-2 py-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full bg-surface-accent dark:bg-surface-accent" />
            ))}
        </div>
    );
});
LoadingSkeleton.displayName = "LoadingSkeleton";

/**
 * Shows "No repositories found" message and "Create from [URL]" option
 * when search query is a valid repository URL.
 */
const CreateFromUrlItem: FC<{
    searchQuery: string;
    onSelect: (url: string, name: string, selectionIndex: number) => void;
}> = memo(({ searchQuery, onSelect }) => {
    const isUrl = isValidRepoUrl(searchQuery);

    const handleSelect = useCallback(() => {
        // Pass URL as both url and name - the actual repo name will be resolved by the API
        onSelect(searchQuery, searchQuery, 0);
    }, [searchQuery, onSelect]);

    // Only show when search query looks like a URL
    if (!isUrl) {
        return null;
    }

    return (
        <>
            <div className="px-3 py-2 text-base opacity-50">No repositories found</div>
            <Command.Item
                className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-start text-base text-content-brand hover:bg-surface-hover data-[selected=true]:bg-surface-hover"
                onSelect={handleSelect}
                // Include the search query in the value so cmdk's filter matches it
                value={searchQuery}
            >
                <IconCirclePlus className="size-4 shrink-0" />
                <span className="shrink-0">Create from</span>
                <span className="truncate font-medium">{searchQuery}</span>
            </Command.Item>
        </>
    );
});
CreateFromUrlItem.displayName = "CreateFromUrlItem";

const AuthenticationRequired: FC<{
    runnerId: string;
    scmHost: string;
    searchQuery: string;
    onSelectUrl: (url: string, name: string, selectionIndex: number) => void;
}> = ({ runnerId, scmHost, searchQuery, onSelectUrl }) => {
    const [clicked, setClicked] = useState(false);
    const { data: authStatus, isLoading } = useIsUserAuthenticatedWithRunnerForHost(runnerId, scmHost, {
        refetchUntilAuthenticated: true,
    });

    const authUrl = authStatus?.type === "AuthenticationRequired" ? authStatus.url : undefined;
    const scmName = authStatus?.scmName ?? "GitHub";
    const isUrl = isValidRepoUrl(searchQuery);

    const handleConnect = useCallback(() => {
        setClicked(true);
        if (authUrl) {
            window.open(authUrl, "_blank");
        }
    }, [authUrl]);

    const handleSelectUrl = useCallback(() => {
        // Pass URL as both url and name - the actual repo name will be resolved by the API
        onSelectUrl(searchQuery, searchQuery, 0);
    }, [searchQuery, onSelectUrl]);

    // If user has entered a valid URL, show the "Create from URL" option
    if (isUrl) {
        return (
            <div className="flex flex-col gap-1 py-1">
                <div className="px-3 py-2 text-base opacity-50">No repositories found</div>
                <Command.Item
                    className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-start text-base text-content-brand hover:bg-surface-hover data-[selected=true]:bg-surface-hover"
                    onSelect={handleSelectUrl}
                    value={searchQuery}
                >
                    <IconCirclePlus className="size-4 shrink-0" />
                    <span className="shrink-0">Create from</span>
                    <span className="truncate font-medium">{searchQuery}</span>
                </Command.Item>
            </div>
        );
    }

    // Default: show connect to SCM option
    return (
        <div className="flex flex-col items-center gap-3 px-3 py-4">
            <p className="text-muted-foreground text-center text-sm">
                Connect to {scmName} to browse your repositories
            </p>
            <Button
                variant="secondary"
                size="sm"
                onClick={handleConnect}
                disabled={!authUrl}
                loading={clicked && isLoading}
                LeadingIcon={() => <IconGithub className="size-4" />}
                data-tracking-id="repository-selector-connect-to-scm"
            >
                {/* wrap this as to not incur the flex spacing between "to" and the scm name */}
                <span>Connect to {scmName}</span>
            </Button>
        </div>
    );
};

const RepositoryList: FC<{
    repositories: PlainSearchRepository[];
    onSelect: (url: string, name: string, selectionIndex: number) => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    onLoadMore?: () => void;
}> = memo(({ repositories, onSelect, hasNextPage, isFetchingNextPage, onLoadMore }) => {
    // useCallback because it is passed to a memoed component
    const handleSelect = useCallback(
        (url: string, name: string, selectionIndex: number) => {
            onSelect(url, name, selectionIndex);
        },
        [onSelect],
    );
    return (
        <>
            {repositories.map(({ name, url }, index) => (
                <RepositoryListItem
                    key={url}
                    name={name}
                    url={url}
                    index={index}
                    onSelect={handleSelect}
                    disabled={false}
                />
            ))}
            {hasNextPage && (
                <Command.Item
                    className="flex w-full cursor-pointer items-center justify-center rounded px-2 py-1.5 text-center text-base text-content-secondary hover:bg-surface-hover data-[selected=true]:bg-surface-hover"
                    onSelect={onLoadMore}
                    disabled={isFetchingNextPage}
                    value="__load_more__"
                >
                    {isFetchingNextPage ? "Loading..." : "Load more"}
                </Command.Item>
            )}
        </>
    );
});
RepositoryList.displayName = "RepositoryList";

const RepositoryListItem: FC<{
    name: string;
    url: string;
    index: number;
    onSelect: (url: string, name: string, index: number) => void;
    disabled: boolean;
}> = memo(({ name, url, index, onSelect, disabled }) => {
    const _onSelect = useCallback(() => {
        onSelect(url, name, index);
    }, [onSelect, url, name, index]);
    return (
        <Command.Item
            className="flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-start text-base hover:bg-surface-hover data-[selected=true]:bg-surface-hover data-[disabled=true]:opacity-50"
            onSelect={_onSelect}
            disabled={disabled}
            value={`${name} ${url}`}
        >
            {name}
        </Command.Item>
    );
});
RepositoryListItem.displayName = "RepositoryListItem";
